import { cartSync, tick } from '@theme/cart-sync';

function fireConfetti(originEl) {
  const run = () => {
    const origin = { y: 0.6 };
    if (originEl) {
      const r = originEl.getBoundingClientRect();
      origin.x = (r.left + r.width / 2) / window.innerWidth;
      origin.y = (r.top + r.height / 2) / window.innerHeight;
    }
    window.confetti({ particleCount: 120, spread: 80, origin });
  };
  if (window.confetti) { run(); return; }
  const s = document.createElement('script');
  s.src = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.3/dist/confetti.browser.min.js';
  s.onload = run;
  document.head.appendChild(s);
}

/**
 * Alza-style Add to Cart button with quantity picker.
 *
 * States:
 *   idle   → shows "Add to cart" button (label from data-label-add attribute)
 *   active → shows [ - ] qty [ + ] picker
 * There is no busy state: taps apply at once and cart-sync batches the
 * requests behind them.
 *
 * Usage:
 *   <alza-add-to-cart
 *     data-variant-id="123"
 *     data-product-url="/products/foo"
 *     data-product-title="Filament PLA černá"
 *     data-max-qty="10"
 *     data-inventory-policy="deny"
 *   ></alza-add-to-cart>
 */
class AlzaAddToCart extends HTMLElement {
  connectedCallback() {
    this.variantId = this.dataset.variantId;
    this.productUrl = this.dataset.productUrl || '#';
    this.productTitle = this.dataset.productTitle || '';
    this.sectionId = this.dataset.sectionId || null;
    this.qty = 0;
    this.atMaxTier = null; // null = initial state not yet known; avoids firing on page load
    this.maxQty = this.computeMaxQty(this.dataset.inventoryPolicy, parseInt(this.dataset.maxQty, 10));

    this.render();
    // Initial cart sync waits for idle: the render above already shows the
    // correct default (qty 0) state, and keeping /cart.js off the loading
    // window helps LCP. All instances share one deduplicated fetch.
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => this.syncWithCart(), { timeout: 3000 });
    } else {
      setTimeout(() => this.syncWithCart(), 300);
    }

    // Re-sync when any alza-add-to-cart instance updates the cart,
    // so multiple instances for the same variant stay in sync.
    this._onCartUpdated = () => this.syncWithCart();
    document.addEventListener('cart:updated', this._onCartUpdated);

    // The REST of the theme announces cart mutations via 'cart:update'
    // (cart page/drawer removals + quantity edits from cart-items-component,
    // theme product forms, quick-order). Without this listener a removal on
    // the cart page leaves every alza widget showing the item as still added.
    this._onThemeCartUpdate = (event) => {
      const detail = event.detail || {};
      // Optimistic events fire BEFORE the server processed the change —
      // syncing now would read the pre-change cart. The settled one follows.
      if (detail.data?.optimistic) return;
      const items = detail.resource?.items;
      if (items) {
        // cart-sync / cart-items ship the full post-change cart — no fetch.
        this.applyCartState(items);
      } else if (detail.data?.source) {
        // A real mutation without cart payload (e.g. gift hint).
        // minStartedAt = event time so an in-flight fetch that predates this
        // change is refused.
        this.syncWithCart(event.timeStamp);
      }
    };
    document.addEventListener('cart:update', this._onThemeCartUpdate);

    // The server refused one of this widget's changes (sold out, stock cap):
    // the settled 'cart:update' before this event already put the widget
    // back to the truth; this only explains why.
    this._onSyncError = (event) => {
      const ids = (event.detail && event.detail.ids) || [];
      if (!ids.some((id) => String(id) === String(this.variantId))) return;
      this.showError(event.detail.message || '');
    };
    document.addEventListener('cart-sync:error', this._onSyncError);

    if (this.sectionId) {
      this._onVariantUpdate = (event) => {
        if (event.detail.sourceId !== this.sectionId) return;
        const variant = event.detail.resource;
        const newId = String(variant?.id || '');
        if (!newId || newId === String(this.variantId)) return;
        this.variantId = newId;
        this.maxQty = this.computeMaxQty(variant?.inventory_policy, variant?.inventory_quantity);
        this.qty = 0;
        this.render();
        this.syncWithCart();
      };
      document.addEventListener('variant:update', this._onVariantUpdate);
    }
  }

  disconnectedCallback() {
    if (this._onCartUpdated) {
      document.removeEventListener('cart:updated', this._onCartUpdated);
    }
    if (this._onThemeCartUpdate) {
      document.removeEventListener('cart:update', this._onThemeCartUpdate);
    }
    if (this._onSyncError) {
      document.removeEventListener('cart-sync:error', this._onSyncError);
    }
    if (this._onVariantUpdate) {
      document.removeEventListener('variant:update', this._onVariantUpdate);
    }
  }

  computeMaxQty(policy, qty) {
    if (policy === 'deny' && Number.isFinite(qty) && qty > 0) return qty;
    return Infinity;
  }

  render() {
    if (this.qty > 0) {
      this.renderPicker();
    } else {
      this.renderButton();
    }
  }

  renderButton() {
    this.innerHTML = `
      <button class="alza-atc__btn" type="button">
        <svg class="alza-atc__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
        </svg>
        <span>${this.dataset.labelAdd || 'Add to cart'}</span>
      </button>
    `;
    this.querySelector('.alza-atc__btn')?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.addToCart();
    });
  }

  getVolumeNudge(qty) {
    const addMore = this.dataset.volAddMore;
    const maxMsg  = this.dataset.volMax;
    if (!addMore || !maxMsg) return null;

    let tiers;
    try { tiers = JSON.parse(this.dataset.volTiers || '[]'); } catch(e) { return null; }
    if (!tiers.length) return null;

    tiers.sort((a, b) => a.qty - b.qty);

    // Filter out tiers that can never be reached given available stock
    if (Number.isFinite(this.maxQty)) {
      tiers = tiers.filter(t => t.qty <= this.maxQty);
    }
    if (!tiers.length) return null;

    const maxTier = tiers[tiers.length - 1];

    if (qty >= maxTier.qty) {
      return { text: maxMsg.replace('__PCT__', maxTier.pct), type: 'max' };
    }

    const nextTier = tiers.find(t => qty < t.qty);
    if (!nextTier) return null;

    return {
      text: addMore.replace('__N__', nextTier.qty - qty).replace('__PCT__', nextTier.pct),
      type: 'progress'
    };
  }

  renderPicker() {
    const nudge = this.getVolumeNudge(this.qty);
    const atMax = Number.isFinite(this.maxQty) && this.qty >= this.maxQty;
    const useInput = this.dataset.input === 'true';
    const maxAttr = Number.isFinite(this.maxQty) ? `max="${this.maxQty}"` : '';

    this.innerHTML = `
      ${nudge ? `<p class="alza-atc__nudge alza-atc__nudge--${nudge.type}">${nudge.text}</p>` : ''}
      <div class="alza-atc__picker">
        <button class="alza-atc__picker-btn alza-atc__picker-btn--minus" type="button">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="5" y1="12" x2="19" y2="12"/></svg>
        </button>
        ${useInput
          ? this.dataset.cartLabel
            ? `<div class="alza-atc__picker-input-wrap">
                 <span class="alza-atc__picker-label">${this.dataset.cartLabel}</span>
                 <input class="alza-atc__picker-input" type="number" value="${this.qty}" min="0" ${maxAttr}>
               </div>`
            : `<input class="alza-atc__picker-input" type="number" value="${this.qty}" min="0" ${maxAttr}>`
          : `<span class="alza-atc__picker-qty">${this.qty}</span>`
        }
        <button class="alza-atc__picker-btn alza-atc__picker-btn--plus" type="button" ${atMax ? 'disabled' : ''} ${atMax ? 'data-at-max' : ''}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        </button>
      </div>
    `;

    this.querySelector('.alza-atc__picker-btn--minus')?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.changeQty(this.qty - 1);
    });
    this.querySelector('.alza-atc__picker-btn--plus')?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.changeQty(this.qty + 1);
    });

    if (useInput) {
      const input = this.querySelector('.alza-atc__picker-input');
      const wrap = this.querySelector('.alza-atc__picker-input-wrap');
      if (wrap) wrap.addEventListener('click', () => input?.focus());

      input?.addEventListener('focus', () => {
        const len = input.value.length;
        // Throws InvalidStateError on type="number" (most browsers): 71 % of
        // the storefront's click errors in Clarity, Oct 2026.
        try { input.setSelectionRange(len, len); } catch {}
      });

      input?.addEventListener('change', (e) => {
        e.stopPropagation();
        const val = parseInt(input.value, 10);
        if (isNaN(val) || val < 0) { input.value = this.qty; return; }
        this.changeQty(val);
      });
      input?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); input.blur(); }
      });
    }
  }

  addToCart() {
    if (Number.isFinite(this.maxQty) && this.qty >= this.maxQty) return;
    this.changeQty(this.qty + 1);
  }

  // Every tap lands in the DOM at once and goes to cart-sync, which batches a
  // burst of taps into as few requests as possible. Nothing is disabled while
  // a request is in flight — the visitor can keep tapping. The badges move
  // optimistically (cart-sync dispatches the delta); the settled 'cart:update'
  // that follows carries the real cart and, if the server refused the change,
  // applyCartState puts the widget back to the truth and showError says why.
  changeQty(newQty) {
    newQty = Math.max(0, Number.isFinite(this.maxQty) ? Math.min(newQty, this.maxQty) : newQty);
    if (newQty === this.qty) return;

    const from = this.qty;
    this.qty = newQty;
    const nowAtMax = this.getVolumeNudge(this.qty)?.type === 'max';
    if (nowAtMax && this.atMaxTier === false && this.dataset.volAddMore) fireConfetti(this);
    this.atMaxTier = nowAtMax;
    this.patch();

    cartSync.setVariant(this.variantId, newQty, from);
  }

  // Update the picker in place when it is already showing: re-creating the
  // buttons under a finger mid-burst drops the press feedback and, on some
  // browsers, the next tap.
  patch() {
    const picker = this.querySelector('.alza-atc__picker');
    if (!picker || this.qty <= 0) { this.render(); return; }

    const nudge = this.getVolumeNudge(this.qty);
    let p = this.querySelector('.alza-atc__nudge');
    if (nudge) {
      if (!p) { p = document.createElement('p'); this.prepend(p); }
      p.className = `alza-atc__nudge alza-atc__nudge--${nudge.type}`;
      if (p.innerHTML !== nudge.text) p.innerHTML = nudge.text;
    } else if (p) {
      p.remove();
    }

    const input = this.querySelector('.alza-atc__picker-input');
    if (input && String(input.value) !== String(this.qty)) input.value = this.qty;
    const span = this.querySelector('.alza-atc__picker-qty');
    if (span && span.textContent !== String(this.qty)) { span.textContent = this.qty; tick(span); }

    const atMax = Number.isFinite(this.maxQty) && this.qty >= this.maxQty;
    const plus = this.querySelector('.alza-atc__picker-btn--plus');
    if (plus) { plus.disabled = atMax; plus.toggleAttribute('data-at-max', atMax); }
  }

  // One shared /cart.js request per moment — a PDP can hold 15+ instances
  // (FBT strip + recommendations) that all re-sync on the same 'cart:updated'
  // event, and firing 15 parallel fetches delays the one response the cart
  // badge is waiting on. Pass minStartedAt to refuse an in-flight request
  // that began before your state change (its response would predate it).
  static fetchCart(minStartedAt = 0) {
    if (!AlzaAddToCart._cartFetch || AlzaAddToCart._cartFetchStart < minStartedAt) {
      AlzaAddToCart._cartFetchStart = performance.now();
      const p = fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then((res) => res.json())
        .finally(() => {
          if (AlzaAddToCart._cartFetch === p) AlzaAddToCart._cartFetch = null;
        });
      AlzaAddToCart._cartFetch = p;
    }
    return AlzaAddToCart._cartFetch;
  }

  async syncWithCart(minStartedAt = 0) {
    try {
      const cart = await AlzaAddToCart.fetchCart(minStartedAt);
      this.applyCartState(cart.items);
    } catch {
      // silent — sync is best-effort
    }
  }

  // Adopts the cart's quantity for this variant — INCLUDING zero, so a
  // removal on the cart page flips the picker back to the Add button.
  applyCartState(items) {
    // A change from this very widget is still queued or in flight — the
    // snapshot predates it; the settled event after it carries the truth.
    if (cartSync.isPending(this.variantId)) return;
    const item = items.find(
      (i) => String(i.variant_id) === String(this.variantId) || String(i.id) === String(this.variantId)
    );
    const qty = item ? item.quantity : 0;
    this.atMaxTier = this.getVolumeNudge(qty)?.type === 'max';
    if (qty === this.qty) return; // no change — keep DOM (and input focus) intact
    this.qty = qty;
    this.render();
  }

  showError(msg) {
    const btn = this.querySelector('.alza-atc__btn');
    if (btn) {
      btn.classList.add('alza-atc__btn--error');
      // Show the error message (or "Vyprodáno" for sold-out)
      const isSoldOut = msg && (msg.includes('vyprodán') || msg.includes('vypredan') || msg.includes('sold out') || msg.includes('out of stock'));
      const span = btn.querySelector('span:not(.alza-atc__spinner)');
      const originalText = span ? span.textContent : '';
      if (span) {
        span.textContent = isSoldOut
          ? (this.dataset.labelSoldOut || 'Sold out')
          : (this.dataset.labelError || 'Error');
      }
      btn.style.background = '#e5e5e5';
      btn.style.color = '#888';
      setTimeout(() => {
        btn.classList.remove('alza-atc__btn--error');
        btn.style.background = '';
        btn.style.color = '';
        if (span) span.textContent = originalText;
      }, 3000);
    }
  }
}

if (!customElements.get('alza-add-to-cart')) {
  customElements.define('alza-add-to-cart', AlzaAddToCart);
}

// WebKit/iOS occasionally skips the custom-element upgrade for some
// <alza-add-to-cart> instances — most often after a back/forward (bfcache)
// navigation — leaving the add-to-cart button unrendered until a refresh.
// `pageshow` fires on the initial load and on every bfcache restore; re-running
// the upgrade there heals any stuck instances. It's a no-op for elements the
// browser already upgraded.
window.addEventListener('pageshow', (e) => {
  customElements.upgrade(document.body);
  // bfcache restore: the DOM — including every widget's qty state — is a
  // snapshot from when the user navigated away, and the cart page they're
  // coming back from is exactly where quantities change. Re-sync from a
  // fresh /cart.js (minStartedAt refuses any pre-navigation response).
  if (e.persisted) {
    document.querySelectorAll('alza-add-to-cart').forEach((el) => {
      if (typeof el.syncWithCart === 'function') el.syncWithCart(e.timeStamp);
    });
  }
});
